import { RobotTypeEnum } from 'api/schema'
import clsx from 'clsx'
import { format } from 'date-fns'
import { ReactComponent as BatteryChargingSvg } from 'images/icons/battery-charging.svg'
import { ReactComponent as BatterySvg } from 'images/icons/battery_half.svg'
import { ReactComponent as CleanWater } from 'images/icons/clean_drop.svg'
import { ReactComponent as DirtyWater } from 'images/icons/dirty_drop.svg'
import { ReactComponent as EmcyButtonSvg } from 'images/icons/emcy_button.svg'
import { ReactComponent as EmergencySvg } from 'images/icons/emergency.svg'
import { ReactComponent as GarbageBin } from 'images/icons/garbage.svg'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { ReactComponent as JoystickSvg } from 'images/icons/joystick.svg'
import { ReactComponent as LocalizationSvg } from 'images/icons/location-bold.svg'
import { ReactComponent as PauseSvg } from 'images/icons/pause.svg'
import { ReactComponent as NoConnectionSvg } from 'images/icons/signal_wifi_off.svg'
import { ReactComponent as ConnectionSvg } from 'images/icons/signal_wifi_on.svg'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import { ROBOT_COMMANDS, ROBOT_MODES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { ROBOT_EQUIPMENT_STATES } from 'utils/constants'
import { DIAGNOSTIC_LEVEL, getDiagnosticStatusText } from 'utils/diagnosticsUtils'
import { readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { VENDOR } from 'utils/vendorDisplayName'

import style from '../RobotDetails.module.css'

const RobotInfo = () => {
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const robot = useSelector(selectRobot)

  const getTextStatus = (percents: number | undefined | null) => {
    if (!percents && percents !== 0) return '-'
    if (percents > 60) return t('robot.good')
    else if (percents > 30) return t('robot.normal')
    else return t('robot.low')
  }

  const getBatteryStatus = (
    charging: boolean | null | undefined,
    power: number | null | undefined,
    charger?: boolean | null
  ) => {
    if (charging === true) return 'Charging'
    if (charger === true) return 'On charger'
    return getTextStatus(power)
  }

  /**
   * Right column (right-aligned): optional current, then SoC.
   * `%` stays last so it does not jump when current appears.
   */
  const formatBatteryMetrics = (
    power: number | null | undefined,
    current: number | null | undefined,
    charging: boolean | null | undefined
  ) => {
    const parts: string[] = []
    if (charging === true && current !== null && current !== undefined && current !== 0) {
      const sign = current >= 0 ? '+' : ''
      parts.push(`${sign}${current.toFixed(2)}A`)
    }
    parts.push(power !== null && power !== undefined ? `${Math.round(power)}%` : '-')
    return parts.join(' · ')
  }

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const liveExecution = robot.robotInfo?.robotRaw
    ? readLiveExecutionFromRobotRead(robot.robotInfo.robotRaw as any)
    : undefined
  const connectionTimeMs = (() => {
    if (robot.robotInfo?.vendor === VENDOR.GAUSIUM) {
      const recordedAt = String(liveExecution?.recorded_at || '').trim()
      if (recordedAt) {
        const parsed = Date.parse(recordedAt)
        if (Number.isFinite(parsed)) return parsed
      }
    }
    if (robot.robotInfo?.vendor === VENDOR.ASBIS && robot.robotInfo.updatedAt) {
      const parsed = Date.parse(robot.robotInfo.updatedAt)
      if (Number.isFinite(parsed)) return parsed
    }
    const telemetrySec = telemetry?.timestamp
    if (typeof telemetrySec === 'number' && Number.isFinite(telemetrySec)) {
      return telemetrySec * 1000
    }
    return null
  })()

  const getRobotBatteryPower = () => {
    if (telemetry?.robotBattery?.power !== undefined && telemetry?.robotBattery?.power !== null) {
      return telemetry.robotBattery.power
    }
    if (telemetry?.batteryPower !== undefined && telemetry?.batteryPower !== null) {
      return telemetry.batteryPower
    }
    return null
  }

  const getRobotBatteryCharging = () => {
    return telemetry?.robotBattery?.charging ?? null
  }

  const hasRobotBatteryData = () => {
    const power = getRobotBatteryPower()
    return power !== null && power !== undefined
  }

  const hasJoystickBatteryData = () => {
    return telemetry?.joystickBattery?.power !== undefined && telemetry?.joystickBattery?.power !== null
  }

  // Get system diagnostics from WebSocket telemetry or fallback to RobotRead from API
  const getSystemDiagnostics = () => {
    // Prefer WebSocket data (more real-time)
    if (telemetry?.system_diagnostics !== null && telemetry?.system_diagnostics !== undefined) {
      return telemetry.system_diagnostics
    }
    // Fallback to API data (from RobotRead)
    if (
      robot.robotInfo?.robotRaw?.system_diagnostics !== null &&
      robot.robotInfo?.robotRaw?.system_diagnostics !== undefined
    ) {
      return robot.robotInfo.robotRaw.system_diagnostics
    }
    return null
  }

  const getLocalizationModeText = (mode: number | undefined) => {
    if (mode === 1) return 'SLAM'
    if (mode === 2) return 'GNSS'
    if (mode === 3) return 'Combined'
    return '-'
  }

  const robotBatteryPower = getRobotBatteryPower()
  const robotBatteryCharging = getRobotBatteryCharging()
  const isAsbis = robot.robotInfo?.vendor === VENDOR.ASBIS
  const isAsbisWelder = isAsbis && (robot.robotInfo?.robot_type || '').toLowerCase().trim() === 'welder'
  const asbisBatteryPower = robot.robotInfo?.batteryPower ?? 83

  if (isAsbis) {
    const asbisVelocity = telemetry?.velocity ?? 0
    return (
      <div className={style.root}>
        <div className={style.tableRow}>
          <div>
            <ConnectionSvg className={style.connectionIcon} />
          </div>
          <div className={style.textAlignLeft}>Connection</div>
          <div className={style.centered}>Online</div>
          <div>
            <div className={style.percents}>
              {connectionTimeMs != null ? format(new Date(connectionTimeMs), 'dd LLLL yyyy') : '-'}
            </div>
            <div className={style.percents}>
              {`Spd: ${Math.round(asbisVelocity * 10) / 10}m/s `}
              {connectionTimeMs != null && format(new Date(connectionTimeMs), 'HH:mm:ss')}
            </div>
          </div>
        </div>
        <div className={style.tableRow}>
          <div>
            <GearSvg />
          </div>
          <div className={style.textAlignLeft}>Diagnostics</div>
          <div className={style.centered}>{getDiagnosticStatusText(DIAGNOSTIC_LEVEL.OK)}</div>
          <div className={style.percents}>-</div>
        </div>
        <div className={style.tableRow}>
          <div>
            <EmergencySvg className={style.emergencyIcon} />
          </div>
          <div className={style.textAlignLeft}>EMCY status</div>
          <div className={style.centered}>Unlocked</div>
          <div className={style.percents}>-</div>
        </div>
        <div className={style.tableRow}>
          <div>
            <EmcyButtonSvg className={style.emergencyIcon} />
          </div>
          <div className={style.textAlignLeft}>EMCY button</div>
          <div className={style.centered}>Released</div>
          <div className={style.percents}>-</div>
        </div>
        {!isAsbisWelder && (
          <div className={clsx(style.tableRow, asbisBatteryPower <= 30 && style.errorRow)}>
            <div>
              <BatterySvg />
            </div>
            <div className={style.textAlignLeft}>Battery</div>
            <div className={style.centered}>{getTextStatus(asbisBatteryPower)}</div>
            <div className={style.percents}>{`${Math.round(asbisBatteryPower)}%`}</div>
          </div>
        )}
      </div>
    )
  }

  const joystickBattery = telemetry?.joystickBattery
  const systemDiagnostics = getSystemDiagnostics()
  const isEmergencyButtonPressed = telemetry?.locks?.danger_button === true
  const isEmergencyStatusLocked =
    telemetry?.cleaningEquipmentState === ROBOT_EQUIPMENT_STATES.EMERGENCY ||
    robot.robotInfo?.robotRaw?.equipment_state === ROBOT_EQUIPMENT_STATES.EMERGENCY

  const localizationInfo = telemetry?.localization_info
  const localizationMode = localizationInfo?.localization_mode
  const localizationModeText = getLocalizationModeText(localizationMode)
  const currentMapName = localizationInfo?.current_lidar_map

  return (
    <div className={style.root}>
      <div className={clsx(style.tableRow, !isOnline && style.offlineRow)}>
        <div>
          {isOnline ? (
            <ConnectionSvg className={style.connectionIcon} />
          ) : (
            <NoConnectionSvg className={style.connectionIcon} />
          )}
        </div>
        <div className={style.textAlignLeft}>Connection</div>
        <div className={style.centered}>{isOnline ? 'Online' : 'Offline'}</div>
        {isOnline ? (
          <div>
            <div className={style.percents}>
              {connectionTimeMs != null ? format(new Date(connectionTimeMs), 'dd LLLL yyyy') : '-'}
            </div>
            <div className={style.percents}>
              {telemetry?.velocity !== undefined && `Spd: ${Math.round(telemetry.velocity * 10) / 10}m/s `}
              {connectionTimeMs != null && format(new Date(connectionTimeMs), 'HH:mm:ss')}
            </div>
          </div>
        ) : (
          <div>
            <div className={style.percents}>Last telemetry</div>
            <div className={style.percents}>
              {(() => {
                const lastAt = robot.robotInfo?.lastTelemetryAt
                if (!lastAt) return '-'
                const parsed = Date.parse(lastAt)
                if (!Number.isFinite(parsed)) return '-'
                return format(new Date(parsed), 'dd LLLL yyyy HH:mm:ss')
              })()}
            </div>
          </div>
        )}
      </div>
      {isOnline && (
        <>
          {systemDiagnostics && (
            <div
              className={clsx(style.tableRow, style.diagnosticsRow, {
                [style.errorRow]: systemDiagnostics.level >= 2,
                [style.warningRow]: systemDiagnostics.level === 1,
              })}
            >
              <div>
                <GearSvg />
              </div>
              <div className={style.textAlignLeft}>Diagnostics</div>
              <div className={clsx(style.centered, style.diagnosticsWrap)}>
                {getDiagnosticStatusText(systemDiagnostics.level)}
              </div>
              <div className={clsx(style.percents, style.diagnosticsWrap)}>
                {systemDiagnostics.level === DIAGNOSTIC_LEVEL.OK
                  ? '-'
                  : systemDiagnostics.name?.trim() || '-'}
              </div>
            </div>
          )}
          <div className={style.tableRow}>
            <div>
              <LocalizationSvg />
            </div>
            <div className={style.textAlignLeft}>Localization</div>
            <div className={style.centered}>{localizationModeText}</div>
            <div className={style.percents}>
              {(localizationMode === 1 || localizationMode === 3) && currentMapName ? currentMapName : '-'}
            </div>
          </div>
          <div className={clsx(style.tableRow, isEmergencyStatusLocked && style.errorRow)}>
            <div>
              <EmergencySvg className={style.emergencyIcon} />
            </div>
            <div className={style.textAlignLeft}>EMCY status</div>
            <div className={style.centered}>{isEmergencyStatusLocked ? 'Locked' : 'Unlocked'}</div>
            <div className={style.percents}>-</div>
          </div>
          {robot.robotInfo?.vendor !== VENDOR.GAUSIUM && (
            <div className={clsx(style.tableRow, isEmergencyButtonPressed && style.errorRow)}>
              <div>
                <EmcyButtonSvg className={style.emergencyIcon} />
              </div>
              <div className={style.textAlignLeft}>EMCY button</div>
              <div className={style.centered}>{isEmergencyButtonPressed ? 'Pressed' : 'Released'}</div>
              <div className={style.percents}>-</div>
            </div>
          )}
          {robot.robotInfo?.activeCommand === ROBOT_COMMANDS.PAUSE &&
            robot.robotInfo?.activeMode === ROBOT_MODES.MODE_AUTOMATIC && (
              <div className={clsx(style.tableRow, style.warningRow)}>
                <div>
                  <PauseSvg className={style.pauseIcon} />
                </div>
                <div className={style.textAlignLeft}>Pause</div>
                <div className={style.centered}>Activated</div>
                <div className={style.percents}></div>
              </div>
            )}
          {hasRobotBatteryData() && (
            <div
              className={clsx(style.tableRow, {
                [style.batteryCharging]: robotBatteryCharging,
                [style.errorRow]: !robotBatteryCharging && robotBatteryPower !== null && robotBatteryPower <= 30,
              })}
            >
              <div>{robotBatteryCharging ? <BatteryChargingSvg /> : <BatterySvg />}</div>
              <div className={style.textAlignLeft}>Battery</div>
              <div className={style.centered}>
                {getBatteryStatus(robotBatteryCharging, robotBatteryPower, telemetry?.charger)}
              </div>
              <div className={style.percents}>
                {formatBatteryMetrics(
                  robotBatteryPower,
                  telemetry?.robotBattery?.current,
                  robotBatteryCharging
                )}
              </div>
            </div>
          )}
          {hasJoystickBatteryData() && (
            <div
              className={clsx(style.tableRow, {
                [style.batteryCharging]: joystickBattery?.charging,
                [style.errorRow]:
                  !joystickBattery?.charging && joystickBattery?.power !== null && (joystickBattery?.power ?? 0) <= 30,
              })}
            >
              <div className={style.joystickIconContainer}>
                <JoystickSvg className={style.joystickIcon} />
              </div>
              <div className={style.textAlignLeft}>Joystick battery</div>
              <div className={style.centered}>
                {getBatteryStatus(joystickBattery?.charging, joystickBattery?.power)}
              </div>
              <div className={style.percents}>
                {formatBatteryMetrics(
                  joystickBattery?.power,
                  joystickBattery?.current,
                  joystickBattery?.charging
                )}
              </div>
            </div>
          )}
          <div
            className={clsx(
              style.tableRow,
              telemetry?.waterTankFilling !== undefined &&
                telemetry?.waterTankFilling !== null &&
                telemetry.waterTankFilling < 10 &&
                style.errorRow
            )}
          >
            <div>
              <CleanWater />
            </div>
            <div className={style.textAlignLeft}>Clean water</div>
            <div className={style.centered}>{getTextStatus(telemetry?.waterTankFilling)}</div>
            <div className={style.percents}>
              {telemetry?.waterTankFilling !== undefined && telemetry?.waterTankFilling !== null
                ? `${Math.round(telemetry.waterTankFilling)}%`
                : '-'}
            </div>
          </div>
          {robot.robotInfo?.robot_type !== RobotTypeEnum.SWEEPER &&
            telemetry?.dirtyWaterTankFilling !== null &&
            telemetry?.dirtyWaterTankFilling !== undefined && (
              <div
                className={clsx(
                  style.tableRow,
                  telemetry.dirtyWaterTankFilling !== undefined &&
                    telemetry.dirtyWaterTankFilling !== null &&
                    telemetry.dirtyWaterTankFilling > 90 && // show red if dirty water tank is full
                    style.errorRow
                )}
              >
                <div>
                  <DirtyWater />
                </div>
                <div className={style.textAlignLeft}>Dirty water</div>
                <div className={style.centered}>
                  {telemetry?.dirtyWaterTankFilling !== undefined &&
                  telemetry?.dirtyWaterTankFilling !== null &&
                  telemetry.dirtyWaterTankFilling > 70
                    ? t('robot.full')
                    : getTextStatus(telemetry?.dirtyWaterTankFilling)}
                </div>
                <div className={style.percents}>
                  {telemetry?.dirtyWaterTankFilling !== undefined
                    ? `${Math.round(telemetry.dirtyWaterTankFilling)}%`
                    : '-'}
                </div>
              </div>
            )}
          {/* Garbage/Waste tank will be shown if data is available */}
          {telemetry?.trashFullness !== null && telemetry?.trashFullness !== undefined && (
            <div className={style.tableRow}>
              <div>
                <GarbageBin />
              </div>
              <div className={style.textAlignLeft}>Waste tank</div>
              <div className={style.centered}>{getTextStatus(telemetry?.trashFullness)}</div>
              <div className={style.percents}>
                {telemetry?.trashFullness !== undefined ? `${Math.round(telemetry.trashFullness)}%` : '-'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default RobotInfo
